import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import classnames from 'classnames';
import {
  DragSource,
  DropTarget,
} from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Input } from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  addSelectedFile,
  removeSelectedFile,
} from '../../actions/Files';

import FileActions from '../File/Actions';
import FileDetails from '../File/Details';
import GridItemLikes from '../GridItemLikes';
import FileTitle from '../File/Title';
import TrashRemainingDays from '../TrashRemainingDays';

import {
  filesTarget,
  filesSource,
} from '../../helpers/DNDFiles';
import ItemTypes from '../../helpers/DNDItemTypes';
import { getFilePageUrl } from '../../helpers/Files';

const FilesMasonryItem = (props) => {
  const intl = useIntl();

  const {
    data: {
      file,
      isSelected,
      displayOnly,
      allowCheck,
      renderDisplayAction,
    },
    width,
    selectFile,
    unselectFile,
    collection,
    connectDragSource,
    connectDropTarget,
    connectDragPreview,
    isDragging,
    share,
    shareCollectionUuid,
    selectedFiles,
  } = props;
  const shareUuid = share ? share.uuid : null;

  useEffect(() => {
    if (connectDragPreview) {
      connectDragPreview(getEmptyImage(), {
        captureDraggingState: true,
      });
    }
  }, [connectDragPreview]);

  let height;
  if (file.height) {
    height = (width / file.width) * file.height;
  }

  const fileTo = file?.to
    || getFilePageUrl({ fileUuid: file.uuid, shareUuid, shareCollectionUuid });

  const isFileSelected = selectedFiles.some(selectedFile => selectedFile.uuid === file.uuid);

  return connectDragSource(connectDropTarget((
    <div
      id={file.uuid}
      className={classnames(
        'masonry-item',
        { dragging: isDragging },
      )}
    >
      <div className="masonry-item-header">
        <div className="masonry-item-header-inner">
          <FileTitle
            className={classnames('masonry-item-title', { 'with-status': file.status })}
            file={file}
            fileTo={fileTo}
          />
          {!displayOnly && (
            <GridItemLikes
              file={file}
              share={share}
              readOnly={!!file.deleted_at}
            />
          )}
          {(allowCheck && (!shareUuid || (share && share.allow_download))) && (
            <Input
              type="checkbox"
              className="input-check check-l"
              id={`file-${file.uuid}`}
              checked={isSelected}
              onChange={() => {
                if (isSelected) {
                  unselectFile(file.uuid);
                } else {
                  selectFile(file);
                }
              }}
              aria-label={isSelected ? intl.formatMessage({ defaultMessage: 'Unselect' }) : intl.formatMessage({ defaultMessage: 'Select' })}
            />
          )}
        </div>
        <FileDetails
          className="masonry-item-details"
          file={file}
          showAccess={!shareUuid && !displayOnly}
        />
      </div>
      <div id={`file-element-${file.uuid}`} className={classnames('masonry-item-wrapper', { selected: isFileSelected })}>
        {(file.type === 'link' && !file.html_code) ? (
          <a href={file.path} className="masonry-item-img-wrapper" title={file.display_name} target="_blank" rel="noopener noreferrer">
            <img
              className="masonry-item-img"
              width="100%"
              src={file.urls.thumbnailmasonry}
              alt={file.display_name}
              style={{
                width,
                height,
              }}
            />
          </a>
        ) : (
          <Link to={fileTo} className="masonry-item-img-wrapper">
            <img
              className="masonry-item-img"
              width="100%"
              src={file.urls.thumbnailmasonry}
              alt={file.display_name}
              style={{
                width,
                height,
              }}
            />
          </Link>
        )}
        {!displayOnly && (
          <div className="masonry-item-body">
            <div className="masonry-item-actions">
              <FileActions
                file={file}
                collection={collection}
                shareUuid={shareUuid}
                share={share}
              />
              <TrashRemainingDays
                file={file}
              />
            </div>
          </div>
        )}
        {displayOnly && renderDisplayAction && (
          <div className="masonry-item-body">
            <div className="masonry-item-actions">
              <ul className="grid-item-actions-list">
                {renderDisplayAction(file, 'masonry')}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  )));
};

const mapStateToProps = (state, props) => ({
  collection: state.Collections.collection,
  canDrag: true,
  id: props.data.file.uuid,
  share: props.data.share,
  shareCollectionUuid: props.data.shareCollectionUuid,
  selectedFiles: state.Files.selectedFiles,
  handleMoveSelectedItems: props.data.handleMoveSelectedItems,
});

const mapDispatchToProps = dispatch => ({
  selectFile: (file) => {
    dispatch(addSelectedFile(file));
  },
  unselectFile: (fileUuid) => {
    dispatch(removeSelectedFile(fileUuid));
  },
});

FilesMasonryItem.propTypes = {
  data: PropTypes.object.isRequired,
  width: PropTypes.number.isRequired,
  selectFile: PropTypes.func.isRequired,
  unselectFile: PropTypes.func.isRequired,
  collection: PropTypes.object,
  share: PropTypes.object,
  shareCollectionUuid: PropTypes.string,
  selectedFiles: PropTypes.array.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  displayOnly: PropTypes.bool,
  allowCheck: PropTypes.bool,
};

FilesMasonryItem.defaultProps = {
  collection: null,
  share: null,
  shareCollectionUuid: null,
  displayOnly: false,
  allowCheck: true,
};

export default
withRouter(connect(mapStateToProps, mapDispatchToProps)(
  DragSource(ItemTypes.FILE, filesSource, (DNDconnect, monitor) => ({
    connectDragSource: DNDconnect.dragSource(),
    connectDragPreview: DNDconnect.dragPreview(),
    isDragging: monitor.isDragging(),
  }))(DropTarget([ItemTypes.FILE, ItemTypes.COLLECTION], filesTarget, DNDconnect => ({
    connectDropTarget: DNDconnect.dropTarget(),
  }))(FilesMasonryItem)),
));
